import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Link,
    Box,
    Alert,
    Chip
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import axios from '../../utils/axios';
import { environment } from '../../config';
import { useAuth } from '../../context/AuthContext';

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [error, setError] = useState('');
    const [isVerificationError, setIsVerificationError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isDevelopment, setIsDevelopment] = useState(false);
    const [emailServiceStatus, setEmailServiceStatus] = useState(null);

    useEffect(() => {
        if (environment !== 'local') {
            return;
        }

        const checkEnvironment = async () => {
            try {
                const response = await axios.get('/api/users/check-email-service');
                setIsDevelopment(response.data.environment === 'development');
                setEmailServiceStatus(response.data);
            } catch (err) {
                setIsDevelopment(false);
            }
        };

        checkEnvironment();
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsVerificationError(false);
        setLoading(true);

        try {
            // Get device information
            const deviceId = localStorage.getItem('deviceId') || Math.random().toString(36).substring(2);
            const deviceName = navigator.userAgent;

            const response = await axios.post('/api/users/login', {
                ...formData,
                deviceId,
                deviceName
            });

            // Store device ID if not already stored
            if (!localStorage.getItem('deviceId')) {
                localStorage.setItem('deviceId', deviceId);
            }

            login(response.data.token);
            navigate('/dashboard');
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Login failed';
            setError(errorMessage);
            
            // Check if it's a verification error
            if (errorMessage.includes('verify') || errorMessage.includes('verification')) {
                setIsVerificationError(true);
            }
        } finally {
            setLoading(false);
        }
    };
    
    const handleResendVerification = () => {
        navigate('/resend-verification', { state: { email: formData.email } });
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Login
                </Typography>

                {isDevelopment && emailServiceStatus && !emailServiceStatus.emailServiceWorking && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Development Mode
                            <Chip 
                                icon={<InfoIcon />}
                                label="Email Service Not Working" 
                                color="primary" 
                                size="small" 
                                sx={{ ml: 1 }}
                            />
                        </Typography>
                        <Typography variant="body2">
                            Email verification is bypassed in development when SendGrid is not configured.
                            New users will be automatically verified.
                        </Typography>
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                        {isVerificationError && (
                            <Box sx={{ mt: 1 }}>
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={handleResendVerification}
                                >
                                    Resend verification email
                                </Button>
                            </Box>
                        )}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        margin="normal"
                        required
                    />
                    <Box sx={{ mt: 3 }}>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={loading}
                        >
                            {loading ? 'Logging in...' : 'Login'}
                        </Button>
                    </Box>
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Link component={RouterLink} to="/register" variant="body2">
                            Don't have an account? Sign up
                        </Link>
                    </Box>
                    <Box sx={{ mt: 1, textAlign: 'center' }}>
                        <Link component={RouterLink} to="/resend-verification" variant="body2">
                            Didn't receive verification email?
                        </Link>
                    </Box>
                    <Box sx={{ mt: 1, textAlign: 'center' }}>
                        <Link component={RouterLink} to="/forgot-password" variant="body2">
                            Forgot your password?
                        </Link>
                    </Box>
                </form>
            </Paper>
        </Container>
    );
};

export default Login; 